import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Phone, Mail, MapPin, MessageCircle, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { COMPANY, SERVICES } from "@/data/content";
import { submitLead } from "@/lib/api";

const schema = z.object({
  full_name: z.string().min(2, "Please enter your full name"),
  email: z.string().email("Please enter a valid email"),
  phone: z.string().min(7, "Please enter a valid phone number"),
  company: z.string().optional(),
  service_interest: z.string().optional(),
  employees_needed: z.string().optional(),
  message: z.string().optional(),
});

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const {
    register, handleSubmit, setValue, watch, reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { full_name: "", email: "", phone: "", company: "", service_interest: "", employees_needed: "", message: "" },
  });

  const onSubmit = async (values) => {
    try {
      await submitLead({ ...values, source: "contact-section" });
      toast.success("Thank you! Our team will reach out within one business day.");
      setSubmitted(true);
      reset();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not submit. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Could not submit. Please try again.");
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 sm:py-24 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Info column */}
          <div className="lg:col-span-5">
            <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-primary">Contact Us</p>
            <h2 className="mt-3 font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight tracking-tight">
              Let's build your workforce together
            </h2>
            <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
              Share your requirement and our team will respond within one business day with a tailored proposal.
            </p>

            <div className="mt-10 space-y-5">
              <ContactRow icon={Phone} label="Call us" testId="contact-info-phone">
                <div className="space-y-1">
                  <a href={`tel:${COMPANY.phoneRaw}`} className="block hover:text-primary">
                    {COMPANY.phone} <span className="text-xs text-slate-500">— Client Enquiry</span>
                  </a>
                  <a href={`tel:${COMPANY.candidatePhoneRaw}`} className="block text-sm text-slate-700 hover:text-primary">
                    {COMPANY.candidatePhone} <span className="text-xs text-slate-500">— Candidates / WhatsApp</span>
                  </a>
                  <div className="text-xs text-slate-500 mt-1">Contact: {COMPANY.contactPerson}</div>
                </div>
              </ContactRow>
              <ContactRow icon={MessageCircle} label="WhatsApp" testId="contact-info-whatsapp">
                <a href={`https://wa.me/${COMPANY.whatsapp}`} target="_blank" rel="noopener noreferrer" className="hover:text-primary">
                  Chat with us instantly
                </a>
              </ContactRow>
              <ContactRow icon={Mail} label="Email" testId="contact-info-email">
                <a href={`mailto:${COMPANY.email}`} className="hover:text-primary">{COMPANY.email}</a>
              </ContactRow>
              <ContactRow icon={MapPin} label="Office" testId="contact-info-address">
                <span>{COMPANY.address.line1}, {COMPANY.address.line2}, {COMPANY.address.city}, {COMPANY.address.state}</span>
              </ContactRow>
            </div>

            <div className="mt-8 rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
              <iframe
                title="Advance HR Technologies — Kalyan West office"
                data-testid="contact-map"
                src="https://www.google.com/maps?q=Kalyan+West,+Maharashtra&output=embed"
                width="100%"
                height="280"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                style={{ border: 0 }}
              />
            </div>
          </div>

          {/* Form column */}
          <div className="lg:col-span-7">
            <div className="rounded-3xl border border-slate-200 bg-slate-50/50 p-6 sm:p-10 shadow-sm">
              {submitted ? (
                <div data-testid="contact-success" className="py-16 text-center">
                  <div className="w-14 h-14 rounded-full bg-emerald-100 mx-auto flex items-center justify-center">
                    <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                  </div>
                  <h3 className="mt-5 font-heading font-bold text-2xl text-slate-900">Thank you!</h3>
                  <p className="mt-3 text-slate-600">We have received your request. Our team will reach out within one business day.</p>
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    data-testid="contact-submit-another"
                    className="mt-6 text-sm font-semibold text-primary hover:text-primary-hover"
                  >
                    Submit another request →
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit(onSubmit)} data-testid="contact-form" className="space-y-5">
                  <h3 className="font-heading font-bold text-2xl text-slate-900">Request a free consultation</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Full Name *" error={errors.full_name?.message}>
                      <Input data-testid="contact-name" placeholder="e.g. Rohit Sharma" {...register("full_name")} />
                    </Field>
                    <Field label="Email *" error={errors.email?.message}>
                      <Input data-testid="contact-email" type="email" placeholder="you@company.com" {...register("email")} />
                    </Field>
                    <Field label="Phone *" error={errors.phone?.message}>
                      <Input data-testid="contact-phone" placeholder="+91 ..." {...register("phone")} />
                    </Field>
                    <Field label="Company">
                      <Input data-testid="contact-company" placeholder="Company name" {...register("company")} />
                    </Field>
                    <Field label="Service Interest">
                      <Select
                        value={watch("service_interest") || ""}
                        onValueChange={(v) => setValue("service_interest", v, { shouldValidate: true })}
                      >
                        <SelectTrigger data-testid="contact-service" className="bg-white">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {SERVICES.map((s) => (
                            <SelectItem key={s.slug} value={s.title} data-testid={`contact-service-option-${s.slug}`}>
                              {s.title}
                            </SelectItem>
                          ))}
                          <SelectItem value="Other" data-testid="contact-service-option-other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Employees Needed">
                      <Select
                        value={watch("employees_needed") || ""}
                        onValueChange={(v) => setValue("employees_needed", v)}
                      >
                        <SelectTrigger data-testid="contact-employees" className="bg-white">
                          <SelectValue placeholder="Approximate count" />
                        </SelectTrigger>
                        <SelectContent>
                          {["1-10", "10-50", "50-200", "200-500", "500+"].map((r) => (
                            <SelectItem key={r} value={r}>{r}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>
                  <Field label="Message">
                    <Textarea
                      data-testid="contact-message"
                      rows={4}
                      placeholder="Tell us about your requirement…"
                      {...register("message")}
                    />
                  </Field>

                  <Button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto bg-primary hover:bg-primary-hover text-white font-semibold px-7 py-6 rounded-lg shadow-sm"
                  >
                    {isSubmitting ? (
                      <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Submitting…</span>
                    ) : "Request Proposal"}
                  </Button>
                  <p className="text-xs text-slate-500">By submitting, you agree to be contacted by our team. We respect your privacy.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon: Icon, label, children, testId }) {
  return (
    <div data-testid={testId} className="flex items-start gap-4">
      <div className="w-11 h-11 shrink-0 rounded-xl bg-primary/10 flex items-center justify-center">
        <Icon className="w-5 h-5 text-primary" strokeWidth={1.6} />
      </div>
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">{label}</div>
        <div className="mt-1 text-sm sm:text-base text-slate-800">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{label}</Label>
      {children}
      {error && <p role="alert" data-testid="contact-field-error" className="text-xs text-destructive error">{error}</p>}
    </div>
  );
}
